import { QueueWithAssignmentCounts } from 'lib-shared-types-api';
import { memo } from 'react';

import { dashboardApi } from 'apis/dashboard';
import { useDashboardRefetchEffect } from 'utils/dashboard';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';
import { DragMoveEvent } from 'utils/useOrderedDragDrop';
import { useServerStorage } from 'utils/useServerStorage';

import { QueueDashboardOrganizationMetrics } from './components/organizationMetrics/queueDashboardOrganizationMetrics';
import { QueueDashboardQueue } from './components/queue/queueDashboardQueue';
import { QueueDashboardTable } from './components/table/queueDashboardTable';

type QueueDashboardQueuesProps = {
  /** The queues to display. */
  queues: readonly QueueWithAssignmentCounts[];
  /** The total number of queues. */
  totalQueues: number;
  /** Handler called when the order of the queues changes. */
  onOrderChange?: (event: DragMoveEvent) => void;
};

export const QueueDashboardQueues = memo(function QueueDashboardQueues({
  queues,
  totalQueues,
  onOrderChange,
}: QueueDashboardQueuesProps): JSX.Element {
  const { params } = useAppSearchParams('/dashboard/queues');
  const storage = useServerStorage('user');
  const { layout, isOrganizationMetricsFavorited = false } =
    storage.data.dashboard ?? {};

  const { metrics, isSkeleton, refetch } =
    dashboardApi.useReadQueueDashboardMetricsQuery(
      {
        queueIds: queues.map((queue) => queue.id),
      },
      {
        skip: !queues.length,
        selectFromResult: ({ data, isUninitialized, isFetching, ...rest }) => ({
          ...rest,
          metrics: data?.data.queue_metrics ?? [],
          isSkeleton: isUninitialized || isFetching,
        }),
      },
    );

  useDashboardRefetchEffect({ refetch });

  /**
   * The organization global metrics are displayed:
   *
   * - When viewing "All queues"
   * - When viewing "Favorites" and `isOrganizationMetricsFavorited` is true
   * - When not searching for anything
   */
  const showOrganizationMetrics =
    (params.type === 'all' ||
      (params.type === 'favourite' && isOrganizationMetricsFavorited)) &&
    !params.q;

  const renderQueues = () => (
    <>
      {showOrganizationMetrics && <QueueDashboardOrganizationMetrics />}

      {queues.map((queue, index) => (
        <QueueDashboardQueue
          key={queue.id}
          queue={queue}
          index={index}
          totalQueues={totalQueues}
          isSkeleton={isSkeleton}
          queueMetrics={metrics.find(({ id }) => id === queue.id)}
          onOrderChange={onOrderChange}
        />
      ))}
    </>
  );

  return layout === 'compact' ? (
    <QueueDashboardTable>{renderQueues()}</QueueDashboardTable>
  ) : (
    renderQueues()
  );
});
